// CountrySelectReact.jsx
import React, { useMemo } from 'react';
import ReactSelect, { components } from 'react-select';
import countryCodes from './countryCodes.json'; // json or js with { name, code, dial_code, emoji/flag }

const OptionWithFlag = (props) => {
  const c = props.data.meta;
  const flag = c?.flag || '';
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>{flag}</span>
        <span style={{ fontSize: 14 }}>{c?.dial_code || c?.code}</span>
      </div>
    </components.Option>
  );
};

const SingleValueWithFlag = (props) => {
  const c = props.data.meta;
  const flag = c?.flag || '';
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 14 }}>{flag}</span>
        <span style={{ fontSize: 14 }}>{c?.dial_code || c?.code}</span>
      </div>
    </components.SingleValue>
  );
};

export default function CountrySelectReact({
  value,            // e.g. "+91" (we keep dial_code as value)
  onChange,         // fn(newDialCode)
  placeholder = 'Code',
  maxMenuHeight = 260,
  maxWidth = 100,
  isDisabled = false,
  variant = 'default',
}) {
  // build options (label contains name so we can still fall back to default filtering if needed)
  const options = useMemo(() => {
    return (countryCodes || []).map(c => ({
      value: c.dial_code ?? c.code ?? c.name,
      // include name in label so default filtering can use it — but we also provide custom filterOption below
      label: `${(c.emoji || c.flag) ? (c.emoji || c.flag) + ' ' : ''}${c.dial_code ?? ''} ${c.name}`,
      meta: c
    }));
  }, [countryCodes]);

  const selected = useMemo(() => options.find(o => o.value === value) || null, [options, value]);

  // custom filter: match input against name, dial_code, or iso code (case-insensitive)
  const filterOption = (candidate, input) => {
    if (!input) return true;
    const q = input.trim().toLowerCase();
    const data = candidate.data?.meta || {};
    const name = (data.name || '').toLowerCase();
    const dial = (data.dial_code || '').toLowerCase();
    const iso = (data.code || '').toLowerCase();

    return (
      name.includes(q) ||
      dial.includes(q) ||
      iso.includes(q) ||
      // also allow searching by label fallback
      (candidate.label || '').toLowerCase().includes(q)
    );
  };

  const isUnderline = variant === 'underline';
  const underlineBorder = '2px solid #e0e0e0';

  const selectStyles = {
    indicatorSeparator: () => ({ display: 'none' }),
    ...(isUnderline && {
      control: (base) => ({
        ...base,
        background: 'transparent',
        border: 'none',
        borderBottom: underlineBorder,
        borderRadius: 0,
        boxShadow: 'none',
        minHeight: 38,
        cursor: 'pointer',
        '&:hover': {
          borderBottom: underlineBorder,
        },
      }),
      valueContainer: (base) => ({
        ...base,
        paddingLeft: 0,
        paddingBottom: '0.225rem',
      }),
      indicatorsContainer: (base) => ({
        ...base,
        paddingBottom: '0.225rem',
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: 16,
        color: '#222',
      }),
      placeholder: (base) => ({
        ...base,
        fontSize: 16,
        color: '#222',
      }),
      menu: (base) => base,
    }),
  };

  return (
    <div
      className={`custom-react-select${isUnderline ? ' custom-react-select--underline' : ''}`}
      style={{
        maxWidth,
        minWidth: 100,
        borderRight: '1px solid #e0e0e0',
        paddingRight: '8px',
        ...(isUnderline ? { alignSelf: 'flex-end' } : {}),
      }}
    >
      <ReactSelect
        options={options}
        value={selected}
        onChange={opt => onChange(opt ? opt.value : '')}
        placeholder={placeholder}
        isSearchable
        isDisabled={isDisabled}
        components={{ Option: OptionWithFlag, SingleValue: SingleValueWithFlag }}
        maxMenuHeight={maxMenuHeight}
        filterOption={filterOption}
        openMenuOnClick
        styles={selectStyles}
      />
    </div>
  );
}
